import React, { useCallback, useEffect, useState } from 'react';
import { Box, Button, FlexBox, Spinner, tokensRaw } from '@vp/swan';
import { bugtracker } from '@99designs/design-services-common';
import { Pagination } from '@99designs/design-services-common';
import { Request } from '@99designs/graph-utils/types';
import { __, getCurrentCulture } from '@99designs/i18n';
import { DashboardEmptyState } from '../dashboard';
import { useListingRequestsForUserLazyQuery } from '../queries/ListingRequests.generated';
import { DraftBriefCard } from './DraftBriefCard';
import { Error } from './Error';
import Toast from './Toast';

const NUMBER_OF_DRAFT_BRIEFS_TO_SHOW = 5;
const MAX_DELETED_TOASTS_SHOWN = 5;

const DraftBriefs: React.FC = () => {
    const [allBriefsExpanded, setAllBriefsExpanded] = useState(false);
    const [deletedToastTitleQueue, setDeletedToastTitleQueue] = useState<string[]>([]);
    const [errorToastsCount, setErrorToastsCount] = useState<number>(0);
    const [currentPage, setCurrentPage] = useState(1);

    const locale = getCurrentCulture();

    const [listRequests, { data, error, loading }] = useListingRequestsForUserLazyQuery();

    const fetchDraftBriefs = useCallback(() => {
        void listRequests({
            fetchPolicy: 'network-only',
            errorPolicy: 'all',
            variables: {
                input: {
                    filters: {
                        locale,
                        state: 'in-progress',
                        hasBrief: true,
                    },
                    size: {
                        limit: NUMBER_OF_DRAFT_BRIEFS_TO_SHOW,
                        offset: (currentPage - 1) * NUMBER_OF_DRAFT_BRIEFS_TO_SHOW,
                    },
                },
            },
        });
    }, [listRequests, locale, currentPage]);

    const totalPages = data?.requestsForUser.totalPages;
    const requests = (data?.requestsForUser?.requests ?? []) as Request[];

    useEffect(() => {
        if (error) {
            bugtracker.notify(error);
        }
    }, [error]);

    // fetchDraftBriefs will update whenever currentPage changes, so it will be called whenever the current page changes
    useEffect(() => {
        void fetchDraftBriefs();
    }, [fetchDraftBriefs]);

    const addToDeletedQueue = useCallback((title: string) => {
        setDeletedToastTitleQueue((prevQueue) => {
            const newQueue = [title, ...prevQueue];
            if (newQueue.length > MAX_DELETED_TOASTS_SHOWN) {
                newQueue.pop();
            }

            return newQueue;
        });
    }, []);

    const addToErrorQueue = useCallback(() => {
        // We allow errors to stack without limit since they can't be "popped"
        // off like deleted toasts, and we don't expect that many in practice.
        setErrorToastsCount((prevCount) => prevCount + 1);
    }, []);

    const removeFromDeletedQueue = useCallback((title: string) => {
        setDeletedToastTitleQueue((prevQueue) => {
            return prevQueue.filter((t) => t !== title);
        });
    }, []);
    const removeFromErrorQueue = useCallback(() => {
        setErrorToastsCount((prevCount) => Math.max(prevCount - 1, 0));
    }, []);

    // Helper to refetch the page after a draft brief is deleted
    const refetchAfterDelete = useCallback(() => {
        // If we're on the last page and only one brief remains, go to previous page if possible
        if (currentPage === totalPages && requests.length === 1 && currentPage > 1) {
            // fetchDraftBriefs will be called in the useEffect when currentPage changes
            setCurrentPage(currentPage - 1);
        } else {
            void fetchDraftBriefs();
        }
    }, [currentPage, totalPages, requests.length, fetchDraftBriefs]);

    if (loading) {
        return (
            <FlexBox alignItems="center" justifyContent="center" mt={10}>
                <Spinner
                    accessibleText={__('Draft briefs loading', {
                        _context:
                            'accessibility text for spinner while waiting for list of draft briefs to load on My Design Services dashboard',
                    })}
                    size={'super'}
                />
            </FlexBox>
        );
    }

    if (error && requests.length === 0) {
        return <Error />;
    }

    if (requests.length === 0) {
        return <DashboardEmptyState state="draft" />;
    }

    const deletedToasts = [...deletedToastTitleQueue].reverse().map((title, index) => {
        return (
            <Toast
                key={`deleted-toast-${title}-${index}`}
                message={__('{{title}} was deleted', {
                    _context:
                        "The alert toast's message when a draft brief is successfully deleted.",
                    title,
                })}
                skin="success"
                onDismiss={() => removeFromDeletedQueue(title)}
            />
        );
    });

    const errorToasts = Array.from({ length: errorToastsCount }).map((_, index) => {
        return (
            <Toast
                key={`error-toast-${index}`}
                message={__("Sorry, couldn't delete draft. Please try again", {
                    _context:
                        "The alert toast's message when there is an error deleting a draft brief.",
                })}
                skin="error"
                onDismiss={removeFromErrorQueue}
            />
        );
    });

    const toastsBox =
        deletedToastTitleQueue.length + errorToastsCount > 0 ? (
            <Box
                style={{
                    bottom: tokensRaw.SwanSemSpace5,

                    // Centre the toasts horizontally:

                    position: 'fixed',
                    left: '50%',
                    transform: 'translateX(-50%)',

                    // Max width as seen on Figma design.

                    maxWidth: '653px',

                    // Ensure there's a margin on small screens.

                    width: `calc(100% - ${tokensRaw.SwanSemSpace5} * 2)`,

                    // Raise over the "draft" callouts:

                    zIndex: tokensRaw.SwanSemZIndexRaised + 1,
                }}
            >
                {deletedToasts}
                {errorToasts}
            </Box>
        ) : null;

    return (
        <>
            <FlexBox flexDirection="column" gap="between-subsections">
                {requests
                    .slice(0, allBriefsExpanded ? requests.length : NUMBER_OF_DRAFT_BRIEFS_TO_SHOW)
                    .map((request) => {
                        return request.brief ? (
                            <DraftBriefCard
                                key={`draft-brief-card-${request.brief.id}`}
                                request={request}
                                refetchDraftBriefs={refetchAfterDelete}
                                addToDeletedQueue={addToDeletedQueue}
                                addToErrorQueue={addToErrorQueue}
                            />
                        ) : null;
                    })}
            </FlexBox>

            {totalPages && (
                <FlexBox alignItems="center" justifyContent="center" mt={5}>
                    <Pagination
                        currentPage={currentPage}
                        totalPages={totalPages}
                        onChange={async (page) => {
                            setCurrentPage(page);
                            window.scrollTo({ top: 0, behavior: 'smooth' });
                        }}
                    />
                </FlexBox>
            )}

            {!allBriefsExpanded && requests.length > NUMBER_OF_DRAFT_BRIEFS_TO_SHOW && (
                <FlexBox alignItems="center" justifyContent="center" mt="between-subsections">
                    <Button skin="secondary" onClick={() => setAllBriefsExpanded(true)}>
                        {__('View all draft briefs')}
                    </Button>
                </FlexBox>
            )}

            {toastsBox}
        </>
    );
};

export { DraftBriefs };
